// File: apps/access-control/access-control-admin/src/app/app.tsx

import React, { useMemo, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { MenuProps } from 'antd';
import { IdpProvider } from './auth/IdpProvider.js';
import { useRehydrateTokens } from '@digiwedge/hooks-auth-web';
import { useTheme } from '@digiwedge/ui-theme';
import { jwtDecode } from 'jwt-decode';
import { readEnvVar } from '../env.runtime.js';

import {
  // Core
  DashboardPage,
  // Tenants
  TenantsPage,
  CreateTenantPage,
  EditTenantPage,
  // User Profiles
  UserProfilesPage,
  // Users
  UsersPage,
  CreateUserPage,
  EditUserPage,
  UserDetailPage,
  // Roles
  RolesPage,
  CreateRolePage,
  EditRolePage,
  RoleDetailPage,
  // Permissions
  PermissionsPage,
  CreatePermissionPage,
  EditPermissionPage,
  PermissionDetailPage,
  // Permission Assignments
  PermissionAssignmentsPage,
  CreatePermissionAssignmentPage,
  EditPermissionAssignmentPage,
  PermissionAssignmentDetailPage,
  // Features
  FeaturesPage,
  CreateFeaturePage,
  EditFeaturePage,
  FeatureDetailPage,
  // Feature Flags
  FeatureFlagsPage,
  CreateFeatureFlagPage,
  EditFeatureFlagPage,
  FeatureFlagDetailPage,
  // Consents
  ConsentsPage,
  CreateConsentPage,
  EditConsentPage,
  ConsentDetailPage,
  // Sessions
  SessionsPage,
  SessionDetailPage,
  // Login Attempts
  LoginAttemptHistoryPage,
  LoginAttemptDetailPage,
  // Invitations
  InvitationsPage,
  CreateInvitationPage,
  EditInvitationPage,
  InvitationDetailPage,
  VoiceProOnboardingWizard,
} from '@digiwedge/access-control-pages';
import { AccessControlApiProvider, CapabilitiesProvider } from '@digiwedge/access-control-pages';
import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';
import { Login, Logout, RegistrationWizard } from '@digiwedge/ui-antd';
import { ForgotPasswordRedirect, ResetPasswordRedirect } from './auth/ForgotResetRedirect.js';
import { usePasskeyLogin } from './auth/usePasskeyLogin.js';

import { AppLayout } from './Layout/AppLayout.js';
import { RequireAuth } from './RequireAuth.js';
import { RequireCapability } from './RequireCapability.js';
import PublicHomePage from './PublicHomePage.js';
import AdminHealthPage from './AdminHealthPage.js';
import SettingsPage from './SettingsPage.js';
import NotificationsPage from './NotificationsPage.js';
import TenantScimConfigPage from './tenants/TenantScimConfigPage.js';
import TenantSamlConfigPage from './tenants/TenantSamlConfigPage.js';
import { accessControlBranding } from './branding.js';

function LoginRoute(): React.ReactElement {
  const passkeyAuth = usePasskeyLogin();

  return (
    <Login
      appName={accessControlBranding.appName}
      heroTitle={accessControlBranding.heroTitle}
      heroSubtitle={accessControlBranding.heroSubtitle}
      heroFeatures={[...accessControlBranding.heroFeatures]}
      placeholderEmail="you@example.com"
      placeholderPassword="••••••••••"
      overlay="none"
      passkeyAuth={passkeyAuth ?? undefined}
      logoLightSrc={accessControlBranding.logoLightSrc}
      logoDarkSrc={accessControlBranding.logoDarkSrc}
      signupPath="/register"
    />
  );
}

function TenantRouteRedirect(): React.ReactElement {
  const { tenantId } = useParams<{ tenantId: string }>();

  if (!tenantId) {
    return <Navigate to="/tenants" replace />;
  }
  return <Navigate to={`/tenants/${tenantId}/edit`} replace />;
}

export function App() {
  // Use centralized theme from @digiwedge/ui-theme (handles system preference + persistence)
  const { mode: themeMode, toggleTheme } = useTheme();

  // Rehydrate tokens from storage on app load so route guards and diagnostics see auth state
  useRehydrateTokens();

  // React Query client for pages/hooks that rely on @tanstack/react-query
  const [queryClient] = useState(() => new QueryClient());

  // Access Control API base URL and tenant scoping
  const apiBase: string | undefined = useMemo(() => {
    const raw = readEnvVar('VITE_ACCESS_CONTROL_BASE_URL') || undefined;
    return raw ? String(raw).replace(/\/$/, '') : undefined;
  }, []);

  // Derive tenant id from JWT claims (supports tenantIds arrays)
  const token = useGlobalStore((s: GlobalState) => s.token);
  const tenantId: string | undefined = useMemo(() => {
    if (!token) return undefined;
    try {
      const claims = jwtDecode<Record<string, unknown>>(token);
      const arrayLike = (claims['tenantIds'] || claims['tenant_ids']) as unknown;
      if (Array.isArray(arrayLike)) {
        const first = arrayLike.find((v) => typeof v === 'string' && v.trim());
        if (typeof first === 'string') return first.trim();
      }
      const single = (claims['tenantId'] || claims['tenant_id'] || claims['tid']) as unknown;
      if (typeof single === 'string' && single.trim()) return single.trim();
    } catch {
      /* ignore */
    }
    return undefined;
  }, [token]);

  return (
    <IdpProvider>
      <QueryClientProvider client={queryClient}>
        <AccessControlApiProvider
          baseUrl={apiBase}
          tenantId={tenantId}
          getAccessToken={() => useGlobalStore.getState().token || undefined}
        >
          <CapabilitiesProvider
            baseUrl={apiBase}
            tenantId={tenantId}
            getAccessToken={() => useGlobalStore.getState().token || undefined}
          >
            <BrowserRouter>
              <Routes>
                {/* Public routes wrapped in AppLayout (theme + header), sidebar hidden */}
                <Route
                  element={
                    <AppLayout
                      themeMode={themeMode}
                      toggleTheme={toggleTheme}
                      hideSidebar
                      hideBreadcrumbs
                      defaultCollapsed
                      navItems={
                        [
                          { label: 'Home', key: '/' },
                          { label: 'Health', key: '/health' },
                        ] as NonNullable<MenuProps['items']>
                      }
                      userMenuItems={[] as NonNullable<MenuProps['items']>}
                    />
                  }
                >
                  <Route path="/login" element={<LoginRoute />} />
                  <Route path="/" element={<PublicHomePage />} />
                  <Route path="/health" element={<AdminHealthPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordRedirect />} />
                  <Route path="/reset-password/:token" element={<ResetPasswordRedirect />} />
                  <Route path="/register" element={<RegistrationWizard />} />
                  <Route path="/logout" element={<Logout />} />
                </Route>

                {/* Protected routes (no base path). Public '/' stays unprotected. */}
                <Route element={<RequireAuth />}>
                  <Route element={<AppLayout themeMode={themeMode} toggleTheme={toggleTheme} />}>
                    <Route path="/dashboard" element={<DashboardPage />} />
                    {/* Tenants */}
                    <Route
                      path="/tenants"
                      element={
                        <RequireCapability feature="TENANT_MGMT" action="read">
                          <TenantsPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/onboarding/voicepro"
                      element={
                        <RequireCapability feature="TENANT_MGMT" action="create">
                          <VoiceProOnboardingWizard />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/tenants/create"
                      element={
                        <RequireCapability feature="TENANT_MGMT" action="create">
                          <CreateTenantPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/tenants/:tenantId/edit"
                      element={
                        <RequireCapability feature="TENANT_MGMT" action="update">
                          <EditTenantPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/tenants/:tenantId"
                      element={
                        <RequireCapability feature="TENANT_MGMT" action="update">
                          <TenantRouteRedirect />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/tenants/:tenantId/scim"
                      element={
                        <RequireCapability feature="TENANT_MGMT" action="update">
                          <TenantScimConfigPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/tenants/:tenantId/saml"
                      element={
                        <RequireCapability feature="TENANT_MGMT" action="update">
                          <TenantSamlConfigPage />
                        </RequireCapability>
                      }
                    />
                    {/* User Profiles */}
                    <Route
                      path="/user-profiles"
                      element={
                        <RequireCapability feature="USER_MGMT" action="read">
                          <UserProfilesPage />
                        </RequireCapability>
                      }
                    />
                    {/* Users */}
                    <Route
                      path="/users"
                      element={
                        <RequireCapability feature="USER_MGMT" action="read">
                          <UsersPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/users/create"
                      element={
                        <RequireCapability feature="USER_MGMT" action="create">
                          <CreateUserPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/users/:userId/edit"
                      element={
                        <RequireCapability feature="USER_MGMT" action="update">
                          <EditUserPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/users/:userId"
                      element={
                        <RequireCapability feature="USER_MGMT" action="read">
                          <UserDetailPage />
                        </RequireCapability>
                      }
                    />

                    {/* Roles */}
                    <Route
                      path="/roles"
                      element={
                        <RequireCapability feature="ROLE_MGMT" action="read">
                          <RolesPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/roles/create"
                      element={
                        <RequireCapability feature="ROLE_MGMT" action="create">
                          <CreateRolePage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/roles/:roleId/edit"
                      element={
                        <RequireCapability feature="ROLE_MGMT" action="update">
                          <EditRolePage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/roles/:roleId"
                      element={
                        <RequireCapability feature="ROLE_MGMT" action="read">
                          <RoleDetailPage />
                        </RequireCapability>
                      }
                    />

                    {/* Permissions */}
                    <Route
                      path="/permissions"
                      element={
                        <RequireCapability feature="PERMISSION_MGMT" action="read">
                          <PermissionsPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/permissions/create"
                      element={
                        <RequireCapability feature="PERMISSION_MGMT" action="create">
                          <CreatePermissionPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/permissions/:permissionId/edit"
                      element={
                        <RequireCapability feature="PERMISSION_MGMT" action="update">
                          <EditPermissionPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/permissions/:permissionId"
                      element={
                        <RequireCapability feature="PERMISSION_MGMT" action="read">
                          <PermissionDetailPage />
                        </RequireCapability>
                      }
                    />

                    {/* Permission Assignments */}
                    <Route
                      path="/permission-assignments"
                      element={
                        <RequireCapability feature="PERMISSION_ASSIGNMENT" action="read">
                          <PermissionAssignmentsPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/permission-assignments/create"
                      element={
                        <RequireCapability feature="PERMISSION_ASSIGNMENT" action="ASSIGN">
                          <CreatePermissionAssignmentPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/permission-assignments/:assignmentId/edit"
                      element={
                        <RequireCapability
                          feature="PERMISSION_ASSIGNMENT"
                          action="UPDATE_ASSIGNMENT"
                        >
                          <EditPermissionAssignmentPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/permission-assignments/:assignmentId"
                      element={
                        <RequireCapability feature="PERMISSION_ASSIGNMENT" action="read">
                          <PermissionAssignmentDetailPage />
                        </RequireCapability>
                      }
                    />

                    {/* Features */}
                    <Route
                      path="/features"
                      element={
                        <RequireCapability feature="FEATURE_MGMT" action="read">
                          <FeaturesPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/features/create"
                      element={
                        <RequireCapability feature="FEATURE_MGMT" action="create">
                          <CreateFeaturePage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/features/:featureId/edit"
                      element={
                        <RequireCapability feature="FEATURE_MGMT" action="update">
                          <EditFeaturePage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/features/:featureId"
                      element={
                        <RequireCapability feature="FEATURE_MGMT" action="read">
                          <FeatureDetailPage />
                        </RequireCapability>
                      }
                    />

                    {/* Feature Flags */}
                    <Route
                      path="/feature-flags"
                      element={
                        <RequireCapability feature="FEATURE_FLAG" action="read">
                          <FeatureFlagsPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/feature-flags/create"
                      element={
                        <RequireCapability feature="FEATURE_FLAG" action="create">
                          <CreateFeatureFlagPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/feature-flags/:flagId/edit"
                      element={
                        <RequireCapability feature="FEATURE_FLAG" action="update">
                          <EditFeatureFlagPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/feature-flags/:flagId"
                      element={
                        <RequireCapability feature="FEATURE_FLAG" action="read">
                          <FeatureFlagDetailPage />
                        </RequireCapability>
                      }
                    />

                    {/* Consents */}
                    <Route
                      path="/consents"
                      element={
                        <RequireCapability feature="CONSENT_MGMT" action="read">
                          <ConsentsPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/consents/create"
                      element={
                        <RequireCapability feature="CONSENT_MGMT" action="create">
                          <CreateConsentPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/consents/:consentId/edit"
                      element={
                        <RequireCapability feature="CONSENT_MGMT" action="update">
                          <EditConsentPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/consents/:consentId"
                      element={
                        <RequireCapability feature="CONSENT_MGMT" action="read">
                          <ConsentDetailPage />
                        </RequireCapability>
                      }
                    />

                    {/* Sessions */}
                    <Route
                      path="/sessions"
                      element={
                        <RequireCapability feature="SESSION_MGMT" action="read">
                          <SessionsPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/sessions/:sessionId"
                      element={
                        <RequireCapability feature="SESSION_MGMT" action="read">
                          <SessionDetailPage />
                        </RequireCapability>
                      }
                    />

                    {/* Login Attempts */}
                    <Route
                      path="/login-attempts"
                      element={
                        <RequireCapability feature="LOGIN_ATTEMPTS" action="read">
                          <LoginAttemptHistoryPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/login-attempts/:attemptId"
                      element={
                        <RequireCapability feature="LOGIN_ATTEMPTS" action="read">
                          <LoginAttemptDetailPage />
                        </RequireCapability>
                      }
                    />

                    {/* Invitations */}
                    <Route
                      path="/invitations"
                      element={
                        <RequireCapability feature="INVITATION_MGMT" action="read">
                          <InvitationsPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/invitations/create"
                      element={
                        <RequireCapability feature="INVITATION_MGMT" action="create">
                          <CreateInvitationPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/invitations/:invitationId/edit"
                      element={
                        <RequireCapability feature="INVITATION_MGMT" action="update">
                          <EditInvitationPage />
                        </RequireCapability>
                      }
                    />
                    <Route
                      path="/invitations/:invitationId"
                      element={
                        <RequireCapability feature="INVITATION_MGMT" action="read">
                          <InvitationDetailPage />
                        </RequireCapability>
                      }
                    />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="*" element={<div>Not Found</div>} />
                  </Route>
                </Route>
              </Routes>
            </BrowserRouter>
          </CapabilitiesProvider>
        </AccessControlApiProvider>
      </QueryClientProvider>
    </IdpProvider>
  );
}

export default App;
